import {
  Directive,
  OnInit,
  OnChanges,
  OnDestroy,
  Host,
  Input,
  Output,
  EventEmitter,
  SimpleChanges
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatGridList } from '@angular/material/grid-list';
import { Observable, BehaviorSubject, Subscription, merge, of } from 'rxjs';
import { filter, switchMap, map } from 'rxjs/operators';

enum MatchedBreakpoint {
  XLarge,
  Large,
  Medium,
  Small,
  XSmall
}

@Directive({
  selector: 'mat-grid-list[appResponsive]',
  exportAs: 'MatGridListResponsive',
})

export class MatGridListResponsiveDirective implements OnInit, OnChanges, OnDestroy {
  @Input('appResponsive') responsive = false;
  @Output() responsiveColsChange: EventEmitter<number> = new EventEmitter<number>();

  private responsive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.responsive);
  private breakPointObservable: Observable<MatchedBreakpoint>;
  private breakPointObserverSubscription = Subscription.EMPTY;

  constructor(
    @Host() private matGridList: MatGridList,
    private breakpointObserver: BreakpointObserver
  ) {
    this.matGridList.cols = 1;

    const buildObservable = (alias: MatchedBreakpoint, breakPoint: string): Observable<MatchedBreakpoint> =>
      this.breakpointObserver.observe(breakPoint)
        .pipe(
          filter(state => state.matches),
          map(() => alias)
        );

    this.breakPointObservable = merge(
      buildObservable(MatchedBreakpoint.XLarge, Breakpoints.XLarge),
      buildObservable(MatchedBreakpoint.Large, Breakpoints.Large),
      buildObservable(MatchedBreakpoint.Medium, Breakpoints.Medium),
      buildObservable(MatchedBreakpoint.Small, Breakpoints.Small),
      buildObservable(MatchedBreakpoint.XSmall, Breakpoints.XSmall)
    );
  }

  ngOnInit() {
    this.breakPointObserverSubscription = this.responsive$
      .pipe(
        switchMap(responsive => responsive ? this.breakPointObservable : of(MatchedBreakpoint.XSmall)),
        map(matchedBreakpoint => this.calculateCols(matchedBreakpoint))
      )
      .subscribe(cols => this.setCols(cols));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['responsive']) {
      this.responsive$.next(changes['responsive'].currentValue);
    }
  }

  ngOnDestroy() {
    if (this.breakPointObserverSubscription) {
      this.breakPointObserverSubscription.unsubscribe();
    }
  }

  private setCols(cols: number) {
    this.responsiveColsChange.emit(cols);
    this.matGridList.cols = cols;
  }

  private calculateCols(matchedBreakpoint: MatchedBreakpoint): number {
    switch (matchedBreakpoint) {
      case MatchedBreakpoint.XLarge:
        return 4;
      case MatchedBreakpoint.Large:
        return 3;
      case MatchedBreakpoint.Medium:
        return 2;
      case MatchedBreakpoint.Small:
        return 1;
      case MatchedBreakpoint.XSmall:
      default:
        return 1;
    }
  }
}

